import React, { useState, useEffect } from "react";
import { sbGetSetting, sbSetSetting } from "../utils/supabaseClient";
import { SVG_ICONS } from "./TablerIconPicker";
import "../styles/CreateTab.css";

const SettingsTab = () => {
  const [activeSettings, setActiveSettings] = useState("pin");
  const [currentPin, setCurrentPin] = useState("");
  const [newPin, setNewPin] = useState("");
  const [confirmPin, setConfirmPin] = useState("");
  const [iosLink, setIosLink] = useState("");
  const [androidLink, setAndroidLink] = useState("");
  const [consultationLink, setConsultationLink] = useState("");
  const [oriEnabled, setOriEnabled] = useState(false);
  const [oriDocuments, setOriDocuments] = useState([]);
  const [docName, setDocName] = useState("");
  const [docContent, setDocContent] = useState("");
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingDocId, setEditingDocId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [editingContent, setEditingContent] = useState("");
  const [showCurrentPin, setShowCurrentPin] = useState(false);
  const [showNewPin, setShowNewPin] = useState(false);
  const [monthlyBudget, setMonthlyBudget] = useState("");
  const [anthropicAdminKey, setAnthropicAdminKey] = useState("");
  const [showAdminKey, setShowAdminKey] = useState(false);

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      const [pin, links, oriKnow, oriFeature, budget, apiKey] = await Promise.all([
        sbGetSetting("origamic-admin-pin"),
        sbGetSetting("origamic-store-links"),
        sbGetSetting("origamic-ori-knowledge"),
        sbGetSetting("ori_feature_enabled"),
        sbGetSetting("api-monthly-budget"),
        sbGetSetting("anthropic-admin-key"),
      ]);
      setCurrentPin(pin || "");
      if (links) {
        setIosLink(links.ios || "");
        setAndroidLink(links.android || "");
        setConsultationLink(links.consultation || "");
      }
      setOriEnabled(oriFeature || false);
      setOriDocuments(Array.isArray(oriKnow) ? oriKnow : []);
      setMonthlyBudget(budget || "");
      setAnthropicAdminKey(apiKey || "");
      setMessage("");
    } catch (err) {
      console.error("Error loading settings:", err);
      setMessage("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePin = async (e) => {
    e.preventDefault();

    // Validation
    if (!newPin.trim() || !confirmPin.trim()) {
      setMessage("Please enter the new PIN in both fields");
      return;
    }

    if (newPin !== confirmPin) {
      setMessage("PINs don't match");
      return;
    }

    if (newPin.length < 4) {
      setMessage("PIN must be at least 4 characters");
      return;
    }

    if (!/^\d+$/.test(newPin)) {
      setMessage("PIN must contain only numbers");
      return;
    }

    setSaving(true);
    try {
      await sbSetSetting("origamic-admin-pin", newPin.trim());
      setMessage("PIN updated successfully!");
      setCurrentPin(newPin.trim());
      setNewPin("");
      setConfirmPin("");

      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error updating PIN:", err);
      setMessage("Failed to update PIN. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveLinks = async (e) => {
    e.preventDefault();

    // Validation
    if (!iosLink.trim() || !androidLink.trim() || !consultationLink.trim()) {
      setMessage("Please fill in all link fields");
      return;
    }

    setSaving(true);
    try {
      const links = {
        ios: iosLink.trim(),
        android: androidLink.trim(),
        consultation: consultationLink.trim(),
      };
      await sbSetSetting("origamic-store-links", links);
      setMessage("Store links updated successfully!");

      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error updating links:", err);
      setMessage("Failed to update links. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleToggleOriFeature = async () => {
    try {
      const newState = !oriEnabled;
      await sbSetSetting("ori_feature_enabled", newState);
      setOriEnabled(newState);
      setMessage(`✓ Ori Knowledge feature ${newState ? "enabled" : "disabled"}!`);
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error toggling Ori feature:", err);
      setMessage("Failed to update feature status");
    }
  };

  const handleAddDocument = async (e) => {
    e.preventDefault();

    if (!docName.trim() || !docContent.trim()) {
      setMessage("Please enter both document name and content");
      return;
    }

    setSaving(true);
    try {
      const newDoc = {
        id: `doc_${Date.now()}`,
        name: docName.trim(),
        content: docContent.trim(),
        addedAt: new Date().toISOString(),
      };

      const updated = [...oriDocuments, newDoc];
      await sbSetSetting("origamic-ori-knowledge", updated);
      setOriDocuments(updated);
      setDocName("");
      setDocContent("");
      setMessage("Document added successfully!");

      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error adding document:", err);
      setMessage("Failed to add document");
    } finally {
      setSaving(false);
    }
  };

  const handleEditDocument = (doc) => {
    console.log("Edit button clicked for doc:", doc);
    setEditingDocId(doc.id);
    setEditingName(doc.name);
    setEditingContent(doc.content);
  };

  const handleSaveEdit = async () => {
    if (!editingName.trim() || !editingContent.trim()) {
      setMessage("Please fill in all fields");
      return;
    }

    setSaving(true);
    try {
      const updated = oriDocuments.map((doc) =>
        doc.id === editingDocId
          ? { ...doc, name: editingName.trim(), content: editingContent.trim() }
          : doc
      );
      await sbSetSetting("origamic-ori-knowledge", updated);
      setOriDocuments(updated);
      setEditingDocId(null);
      setMessage("Document updated successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error updating document:", err);
      setMessage("Failed to update document");
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingDocId(null);
    setEditingName("");
    setEditingContent("");
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm("Delete this document?")) return;

    try {
      const updated = oriDocuments.filter((doc) => doc.id !== docId);
      await sbSetSetting("origamic-ori-knowledge", updated);
      setOriDocuments(updated);
      setMessage("Document deleted!");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error deleting document:", err);
    }
  };

  const handleSaveBudget = async (e) => {
    e.preventDefault();

    if (!monthlyBudget.trim()) {
      setMessage("Please enter a budget amount");
      return;
    }

    const budgetAmount = parseFloat(monthlyBudget);
    if (isNaN(budgetAmount) || budgetAmount <= 0) {
      setMessage("Budget must be a positive number");
      return;
    }

    setSaving(true);
    try {
      await sbSetSetting("api-monthly-budget", monthlyBudget.trim());
      setMessage("Monthly budget updated successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error updating budget:", err);
      setMessage("Failed to update budget");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAdminKey = async (e) => {
    e.preventDefault();

    if (!anthropicAdminKey.trim()) {
      setMessage("Please enter your Anthropic Admin API key");
      return;
    }

    if (!anthropicAdminKey.startsWith("sk-ant-admin")) {
      setMessage("Admin API key must start with 'sk-ant-admin'");
      return;
    }

    setSaving(true);
    try {
      await sbSetSetting("anthropic-admin-key", anthropicAdminKey.trim());
      setMessage("Anthropic Admin API key saved successfully!");
      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error saving API key:", err);
      setMessage("Failed to save API key");
    } finally {
      setSaving(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setSaving(true);
      const text = await file.text();
      const fileName = file.name.replace(/\.[^/.]+$/, "");

      const newDoc = {
        id: `doc_${Date.now()}`,
        name: fileName,
        content: text,
        addedAt: new Date().toISOString(),
      };

      const updated = [...oriDocuments, newDoc];
      await sbSetSetting("origamic-ori-knowledge", updated);
      setOriDocuments(updated);
      setMessage(`✓ File "${file.name}" uploaded!`);

      setTimeout(() => setMessage(""), 3000);
    } catch (err) {
      console.error("Error uploading file:", err);
      setMessage("Failed to upload file");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading">Loading settings...</div>;
  }

  const settingsSections = [
    { id: "pin", label: "PIN Management", icon: "key" },
    { id: "links", label: "External Links", icon: "link" },
    { id: "ori", label: "Ori Knowledge", icon: "brain" },
    { id: "billing", label: "API Budget", icon: "trending-up" },
  ];

  return (
    <div style={{ display: "flex", height: "100%", gap: "0" }}>
      {/* Left Navigation */}
      <div
        style={{
          width: "200px",
          borderRight: "1px solid rgba(59, 130, 246, 0.2)",
          padding: "0",
          background: "rgba(5, 14, 26, 0.5)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <h3
          style={{
            padding: "20px",
            margin: "0",
            fontSize: "14px",
            color: "#5B9EF0",
            fontWeight: "600",
            borderBottom: "1px solid rgba(59, 130, 246, 0.2)",
          }}
        >
          Settings
        </h3>
        <nav style={{ flex: 1, display: "flex", flexDirection: "column", padding: "12px" }}>
          {settingsSections.map((section) => (
            <button
              key={section.id}
              onClick={() => setActiveSettings(section.id)}
              style={{
                padding: "12px 14px",
                background:
                  activeSettings === section.id ? "rgba(59, 130, 246, 0.2)" : "transparent",
                border: activeSettings === section.id ? "1px solid rgba(59, 130, 246, 0.4)" : "none",
                borderRadius: "6px",
                color: activeSettings === section.id ? "#5B9EF0" : "#7A9CC4",
                cursor: "pointer",
                fontSize: "13px",
                fontWeight: activeSettings === section.id ? "600" : "400",
                textAlign: "left",
                marginBottom: "8px",
                transition: "all 0.2s",
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              {section.icon && SVG_ICONS[section.icon] && (
                <span style={{ display: "flex", alignItems: "center" }}>
                  {SVG_ICONS[section.icon]({ size: 16, color: activeSettings === section.id ? "#5B9EF0" : "#7A9CC4" })}
                </span>
              )}
              {section.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Right Content */}
      <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
        <div style={{ width: "100%", maxWidth: "100%" }}>
          {/* PIN Section */}
          {activeSettings === "pin" && (
            <>
        {/* Current PIN Display */}
        <div className="form-group" style={{ marginBottom: "32px" }}>
          <label>Current PIN</label>
          <div
            style={{
              display: "flex",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <input
              type={showCurrentPin ? "text" : "password"}
              value={currentPin}
              disabled
              style={{
                flex: 1,
                background: "#050e1a",
                border: "1px solid rgba(59, 130, 246, 0.15)",
                borderRadius: "8px",
                padding: "12px",
                color: "#5B9EF0",
                fontFamily: "monospace",
                fontSize: "14px",
              }}
            />
            <button
              type="button"
              onClick={() => setShowCurrentPin(!showCurrentPin)}
              style={{
                padding: "8px 14px",
                background: "transparent",
                border: "1px solid rgba(59, 130, 246, 0.3)",
                color: "#5B9EF0",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: "600",
              }}
            >
              {showCurrentPin ? "Hide" : "Show"}
            </button>
          </div>
        </div>

        {/* Change PIN Form */}
        <form onSubmit={handleChangePin}>
          <h3 style={{ color: "#5B9EF0", marginBottom: "16px" }}>Change PIN</h3>

          <div className="form-group">
            <label>New PIN (numbers only, 4+ digits)</label>
            <input
              type={showNewPin ? "text" : "password"}
              value={newPin}
              onChange={(e) => setNewPin(e.target.value.replace(/\D/g, ""))}
              placeholder="e.g., 456789"
              maxLength="10"
              disabled={saving}
              style={{
                width: "100%",
                padding: "12px",
                background: "#050e1a",
                border: "1px solid rgba(59, 130, 246, 0.15)",
                borderRadius: "8px",
                color: "white",
                fontFamily: "monospace",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div className="form-group">
            <label>Confirm PIN</label>
            <input
              type={showNewPin ? "text" : "password"}
              value={confirmPin}
              onChange={(e) => setConfirmPin(e.target.value.replace(/\D/g, ""))}
              placeholder="Re-enter the new PIN"
              maxLength="10"
              disabled={saving}
              style={{
                width: "100%",
                padding: "12px",
                background: "#050e1a",
                border: "1px solid rgba(59, 130, 246, 0.15)",
                borderRadius: "8px",
                color: "white",
                fontFamily: "monospace",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div
            style={{
              display: "flex",
              gap: "10px",
              marginBottom: "20px",
            }}
          >
            <button
              type="button"
              onClick={() => setShowNewPin(!showNewPin)}
              style={{
                padding: "10px 16px",
                background: "transparent",
                border: "1px solid rgba(59, 130, 246, 0.3)",
                color: "#5B9EF0",
                borderRadius: "8px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: "600",
              }}
            >
              {showNewPin ? "Hide PIN" : "Show PIN"}
            </button>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              disabled={saving || !newPin || !confirmPin}
              className="btn-primary"
            >
              {saving ? "Updating..." : "Update PIN"}
            </button>
          </div>

          {message && (
            <div
              className={`message ${
                message.includes("success") || !message.includes("❌") ? "success" : "error"
              }`}
            >
              {message}
            </div>
          )}
        </form>

        {/* PIN Info */}
        <div
          style={{
            marginTop: "32px",
            padding: "16px",
            background: "rgba(59, 130, 246, 0.1)",
            border: "1px solid rgba(59, 130, 246, 0.3)",
            borderRadius: "8px",
            fontSize: "13px",
            color: "#7A9CC4",
            lineHeight: "1.6",
          }}
        >
          <strong>PIN Security Note:</strong>
          <ul style={{ margin: "8px 0 0 20px", paddingLeft: "0" }}>
            <li>PIN must be 4+ digits (numbers only)</li>
            <li>Change the PIN regularly for security</li>
            <li>Don't share your PIN with unauthorized users</li>
            <li>Default PIN is 123456 if not set</li>
          </ul>
        </div>
            </>
          )}

          {/* Links Section */}
          {activeSettings === "links" && (
            <>
              <form onSubmit={handleSaveLinks} style={{ marginTop: "0" }}>
              <h3 style={{ color: "#5B9EF0", marginBottom: "16px" }}>App Store Links</h3>

              <div className="form-group">
            <label>iOS App Store Link</label>
            <input
              type="url"
              value={iosLink}
              onChange={(e) => setIosLink(e.target.value)}
              placeholder="https://apps.apple.com/..."
              disabled={saving}
              style={{
                width: "100%",
                padding: "12px",
                background: "#050e1a",
                border: "1px solid rgba(59, 130, 246, 0.15)",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div className="form-group">
            <label>Android Play Store Link</label>
            <input
              type="url"
              value={androidLink}
              onChange={(e) => setAndroidLink(e.target.value)}
              placeholder="https://play.google.com/store/apps/..."
              disabled={saving}
              style={{
                width: "100%",
                padding: "12px",
                background: "#050e1a",
                border: "1px solid rgba(59, 130, 246, 0.15)",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div className="form-group">
            <label>Consultation Scheduling Link</label>
            <input
              type="url"
              value={consultationLink}
              onChange={(e) => setConsultationLink(e.target.value)}
              placeholder="https://origamicsolutions.com/schedule-a-meeting"
              disabled={saving}
              style={{
                width: "100%",
                padding: "12px",
                background: "#050e1a",
                border: "1px solid rgba(59, 130, 246, 0.15)",
                borderRadius: "8px",
                color: "white",
                fontSize: "14px",
                boxSizing: "border-box",
              }}
            />
          </div>

          <div className="form-actions">
            <button
              type="submit"
              disabled={saving || !iosLink || !androidLink || !consultationLink}
              className="btn-primary"
            >
              {saving ? "Updating..." : "Update Links"}
            </button>
          </div>

          {message && message.includes("link") && (
            <div
              className={`message ${
                message.includes("success") || !message.includes("❌") ? "success" : "error"
              }`}
            >
              {message}
            </div>
              )}
              </form>

              {/* Links Info */}
              <div
                style={{
                  marginTop: "32px",
                  padding: "16px",
                  background: "rgba(34, 197, 94, 0.1)",
                  border: "1px solid rgba(34, 197, 94, 0.3)",
                  borderRadius: "8px",
                  fontSize: "13px",
                  color: "#86EFAC",
                  lineHeight: "1.6",
                }}
              >
                <strong>Store Links Note:</strong>
                <ul style={{ margin: "8px 0 0 20px", paddingLeft: "0" }}>
                  <li>iOS link directs users to the App Store</li>
                  <li>Android link directs users to Google Play Store</li>
                  <li>Consultation link is used for "Book a Consultation" buttons</li>
                  <li>All links must be valid URLs</li>
                </ul>
              </div>
            </>
          )}

          {/* Ori Knowledge Section */}
          {activeSettings === "ori" && (
        <div style={{ marginTop: "0" }}>
          <h3 style={{ color: "#5B9EF0", marginBottom: "16px", display: "flex", alignItems: "center", gap: "10px" }}>
            {SVG_ICONS["brain"] && SVG_ICONS["brain"]({ size: 20, color: "#5B9EF0" })}
            Ori Knowledge Management
          </h3>

          {/* Feature Toggle */}
          <div style={{ marginBottom: "24px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
              }}
            >
              <label style={{ margin: 0, fontWeight: "600" }}>
                Enable Ori Knowledge Feature
              </label>
              <div
                onClick={handleToggleOriFeature}
                style={{
                  width: "50px",
                  height: "28px",
                  background: oriEnabled ? "#3b82f6" : "#6b7280",
                  borderRadius: "20px",
                  position: "relative",
                  cursor: "pointer",
                  transition: "background 0.2s",
                  display: "flex",
                  alignItems: "center",
                  paddingRight: oriEnabled ? "4px" : "20px",
                }}
              >
                <div
                  style={{
                    width: "24px",
                    height: "24px",
                    background: "white",
                    borderRadius: "50%",
                    position: "absolute",
                    left: oriEnabled ? "23px" : "2px",
                    transition: "left 0.2s",
                  }}
                />
              </div>
              <span style={{ fontSize: "12px", color: "#7A9CC4" }}>
                {oriEnabled ? "Enabled" : "Disabled"}
              </span>
            </div>
          </div>

          {/* Add Document Form */}
          <form onSubmit={handleAddDocument} style={{ marginBottom: "24px" }}>
            <h4 style={{ color: "#5B9EF0", marginBottom: "12px" }}>Add Document</h4>

            <div className="form-group">
              <label>Document Name</label>
              <input
                type="text"
                value={docName}
                onChange={(e) => setDocName(e.target.value)}
                placeholder="e.g., Company Culture, Product Guide"
                disabled={saving}
                style={{
                  width: "100%",
                  padding: "12px",
                  background: "#050e1a",
                  border: "1px solid rgba(59, 130, 246, 0.15)",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div className="form-group">
              <label>Content (Paste text or upload file)</label>
              <textarea
                value={docContent}
                onChange={(e) => setDocContent(e.target.value)}
                placeholder="Paste your document content here..."
                rows="8"
                disabled={saving}
                style={{
                  width: "100%",
                  padding: "12px",
                  background: "#050e1a",
                  border: "1px solid rgba(59, 130, 246, 0.15)",
                  borderRadius: "8px",
                  color: "white",
                  fontSize: "14px",
                  fontFamily: "inherit",
                  boxSizing: "border-box",
                }}
              />
            </div>

            <div style={{ display: "flex", gap: "10px", marginBottom: "16px" }}>
              <button
                type="submit"
                disabled={saving || !docName || !docContent}
                className="btn-primary"
                style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: "8px" }}
              >
                <span>+</span>
                {saving ? "Adding..." : "Add Document"}
              </button>
              <label
                style={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  padding: "10px 16px",
                  background: "rgba(99, 102, 241, 0.15)",
                  border: "1px solid rgba(99, 102, 241, 0.3)",
                  borderRadius: "8px",
                  color: "#818cf8",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "14px",
                }}
              >
                {SVG_ICONS["folder"] && SVG_ICONS["folder"]({ size: 16, color: "#818cf8" })}
                Upload File
                <input
                  type="file"
                  accept=".txt,.md,.pdf,.doc,.docx"
                  onChange={handleFileUpload}
                  disabled={saving}
                  style={{ display: "none" }}
                />
              </label>
            </div>
          </form>

          {/* Documents List */}
          {oriDocuments.length > 0 && (
            <div>
              <h4 style={{ color: "#5B9EF0", marginBottom: "12px" }}>
                Documents ({oriDocuments.length})
              </h4>
              <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                {oriDocuments.map((doc) => (
                  <div
                    key={doc.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      padding: "12px",
                      background: "rgba(59, 130, 246, 0.1)",
                      border: "1px solid rgba(59, 130, 246, 0.3)",
                      borderRadius: "8px",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: "600", color: "#5B9EF0" }}>
                        {doc.name}
                      </div>
                      <div style={{ fontSize: "12px", color: "#7A9CC4", marginTop: "4px" }}>
                        {doc.content.length} characters
                        {doc.addedAt && ` • Added ${new Date(doc.addedAt).toLocaleDateString()}`}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "8px" }}>
                      <button
                        onClick={() => handleEditDocument(doc)}
                        style={{
                          padding: "6px 12px",
                          background: "rgba(59, 130, 246, 0.15)",
                          border: "1px solid rgba(59, 130, 246, 0.3)",
                          color: "#5B9EF0",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteDocument(doc.id)}
                        style={{
                          padding: "6px 12px",
                          background: "rgba(248, 113, 113, 0.15)",
                          border: "1px solid rgba(248, 113, 113, 0.3)",
                          color: "#f87171",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "12px",
                          fontWeight: "600",
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            )}

            {/* Edit Document Form */}
            {editingDocId && (
              <div style={{ marginTop: "24px", padding: "16px", background: "rgba(59, 130, 246, 0.1)", border: "1px solid rgba(59, 130, 246, 0.3)", borderRadius: "8px" }}>
                <h4 style={{ color: "#5B9EF0", marginBottom: "12px" }}>Edit Document</h4>

                <div className="form-group">
                  <label>Document Name</label>
                  <input
                    type="text"
                    value={editingName}
                    onChange={(e) => setEditingName(e.target.value)}
                    placeholder="Document name..."
                    disabled={saving}
                    style={{
                      width: "100%",
                      padding: "12px",
                      background: "#050e1a",
                      border: "1px solid rgba(59, 130, 246, 0.15)",
                      borderRadius: "8px",
                      color: "white",
                      fontSize: "14px",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div className="form-group">
                  <label>Content</label>
                  <textarea
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                    placeholder="Document content..."
                    rows="8"
                    disabled={saving}
                    style={{
                      width: "100%",
                      padding: "12px",
                      background: "#050e1a",
                      border: "1px solid rgba(59, 130, 246, 0.15)",
                      borderRadius: "8px",
                      color: "white",
                      fontSize: "14px",
                      fontFamily: "inherit",
                      boxSizing: "border-box",
                    }}
                  />
                </div>

                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    onClick={handleSaveEdit}
                    disabled={saving}
                    style={{
                      flex: 1,
                      padding: "10px 16px",
                      background: "#3b82f6",
                      color: "white",
                      border: "none",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    {saving ? "Saving..." : "Save Changes"}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    disabled={saving}
                    style={{
                      flex: 1,
                      padding: "10px 16px",
                      background: "rgba(59, 130, 246, 0.15)",
                      color: "#5B9EF0",
                      border: "1px solid rgba(59, 130, 246, 0.3)",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: "600",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Ori Info */}
            <div
              style={{
                marginTop: "32px",
                padding: "16px",
                background: "rgba(168, 85, 247, 0.1)",
                border: "1px solid rgba(168, 85, 247, 0.3)",
                borderRadius: "8px",
                fontSize: "13px",
                color: "#d8b4fe",
                lineHeight: "1.6",
              }}
            >
              <strong>Ori Knowledge Note:</strong>
              <ul style={{ margin: "8px 0 0 20px", paddingLeft: "0" }}>
                <li>Documents are used by Ori to provide context-aware responses</li>
                <li>Supports text, markdown, PDF, and document uploads</li>
                <li>Each document needs a unique name</li>
                <li>Disable feature to prevent Ori from accessing knowledge base</li>
                <li>Knowledge base is used to enhance Ori's accuracy</li>
              </ul>
            </div>
          </div>
          )}

          {/* Billing Section */}
          {activeSettings === "billing" && (
            <>
              <h2 style={{ margin: "0 0 20px 0", color: "#5B9EF0" }}>API Configuration</h2>

              {/* Monthly Budget */}
              <form onSubmit={handleSaveBudget} style={{ display: "flex", flexDirection: "column", gap: "16px", marginBottom: "32px" }}>
                <div>
                  <label style={{ display: "block", marginBottom: "8px", color: "#7A9CC4", fontSize: "13px", fontWeight: "600" }}>
                    Monthly Budget (USD)
                  </label>
                  <input
                    type="number"
                    value={monthlyBudget}
                    onChange={(e) => setMonthlyBudget(e.target.value)}
                    placeholder="5"
                    step="0.01"
                    min="0"
                    style={{
                      width: "100%",
                      padding: "10px 12px",
                      background: "rgba(5, 14, 26, 0.8)",
                      border: "1px solid rgba(59, 130, 246, 0.2)",
                      borderRadius: "6px",
                      color: "#9DB4D4",
                      fontSize: "14px",
                      boxSizing: "border-box",
                    }}
                  />
                  <p style={{ margin: "8px 0 0 0", fontSize: "12px", color: "#7A9CC4" }}>
                    This value is used in the API Billing report to track spending and show budget burn-down.
                  </p>
                </div>

                <div style={{
                  padding: "12px 16px",
                  background: "rgba(59, 130, 246, 0.08)",
                  border: "1px solid rgba(59, 130, 246, 0.2)",
                  borderRadius: "6px",
                  fontSize: "12px",
                  color: "#7A9CC4",
                }}>
                  <strong style={{ color: "#5B9EF0" }}>Current Budget:</strong> ${parseFloat(monthlyBudget || 0).toFixed(2)}/month
                </div>

                <button
                  type="submit"
                  disabled={saving}
                  className="btn-primary"
                  style={{
                    alignSelf: "flex-start",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    minWidth: "140px",
                    justifyContent: "center"
                  }}
                >
                  {saving ? (
                    <>
                      <span style={{ display: "inline-block", animation: "spin 1s linear infinite" }}>⟳</span>
                      Saving...
                    </>
                  ) : message.includes("successfully") ? (
                    <>
                      <span style={{ color: "#10B981", fontSize: "16px" }}>✓</span>
                      Saved!
                    </>
                  ) : (
                    "Save Budget"
                  )}
                </button>

                <style>{`
                  @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                  }
                `}</style>
              </form>

              {/* Anthropic Admin API Key */}
              <form onSubmit={handleSaveAdminKey} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <h3 style={{ color: "#5B9EF0", marginBottom: "12px", marginTop: "0" }}>Anthropic Admin API Key</h3>

                <div>
                  <label style={{ display: "block", marginBottom: "8px", color: "#7A9CC4", fontSize: "13px", fontWeight: "600" }}>
                    Admin API Key (sk-ant-admin-...)
                  </label>
                  <div style={{ display: "flex", gap: "10px", alignItems: "flex-end" }}>
                    <input
                      type={showAdminKey ? "text" : "password"}
                      value={anthropicAdminKey}
                      onChange={(e) => setAnthropicAdminKey(e.target.value)}
                      placeholder="sk-ant-admin-..."
                      disabled={saving}
                      style={{
                        flex: 1,
                        padding: "10px 12px",
                        background: "rgba(5, 14, 26, 0.8)",
                        border: "1px solid rgba(59, 130, 246, 0.2)",
                        borderRadius: "6px",
                        color: "#9DB4D4",
                        fontSize: "14px",
                        boxSizing: "border-box",
                        fontFamily: "monospace",
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowAdminKey(!showAdminKey)}
                      style={{
                        padding: "8px 14px",
                        background: "transparent",
                        border: "1px solid rgba(59, 130, 246, 0.3)",
                        color: "#5B9EF0",
                        borderRadius: "6px",
                        cursor: "pointer",
                        fontSize: "12px",
                        fontWeight: "600",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {showAdminKey ? "Hide" : "Show"}
                    </button>
                  </div>
                  <p style={{ margin: "8px 0 0 0", fontSize: "12px", color: "#7A9CC4" }}>
                    Get this key from Anthropic Console → Settings → Admin Keys
                  </p>
                </div>

                {anthropicAdminKey && (
                  <div style={{
                    padding: "12px 16px",
                    background: "rgba(34, 197, 94, 0.08)",
                    border: "1px solid rgba(34, 197, 94, 0.2)",
                    borderRadius: "6px",
                    fontSize: "12px",
                    color: "#86EFAC",
                  }}>
                    <strong>Key saved:</strong> {anthropicAdminKey.substring(0, 20)}...
                  </div>
                )}

                <button
                  type="submit"
                  disabled={saving || !anthropicAdminKey}
                  className="btn-primary"
                  style={{
                    alignSelf: "flex-start",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    minWidth: "160px",
                    justifyContent: "center"
                  }}
                >
                  {saving ? (
                    <>
                      <span style={{ display: "inline-block", animation: "spin 1s linear infinite" }}>⟳</span>
                      Saving...
                    </>
                  ) : message.includes("successfully") && message.includes("API") ? (
                    <>
                      <span style={{ color: "#10B981", fontSize: "16px" }}>✓</span>
                      Saved!
                    </>
                  ) : (
                    "Save API Key"
                  )}
                </button>
              </form>

              <div
                style={{
                  marginTop: "32px",
                  padding: "16px",
                  background: "rgba(91, 158, 240, 0.1)",
                  border: "1px solid rgba(91, 158, 240, 0.3)",
                  borderRadius: "8px",
                  fontSize: "13px",
                  color: "#a5d6ff",
                  lineHeight: "1.6",
                }}
              >
                <strong>API Budget Note:</strong>
                <ul style={{ margin: "8px 0 0 20px", paddingLeft: "0" }}>
                  <li>Budget is used to calculate spending in the API Billing report</li>
                  <li>The billing report shows monthly API usage and costs by model</li>
                  <li>Update this value to match your monthly API budget allocation</li>
                  <li>Changes are saved to the database and reflected in the billing report immediately</li>
                </ul>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsTab;
